import { useState, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Warning,
  Person,
  AccountTree,
  Settings,
  Cloud,
  Email,
} from '@mui/icons-material';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import {
  useCompany,
  useCompanyAccounts,
  useCompanyUsers,
  useForceDeleteCompany,
} from '@/lib/query/hooks/useCompanies';
import { CompanyAccountList, CompanyUserList, EmailTemplateViewDialog } from '@/components/mise';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `company-tab-${index}`,
    'aria-controls': `company-tabpanel-${index}`,
  };
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [tabValue, setTabValue] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = useState(false);

  const forceDeleteCompany = useForceDeleteCompany();

  const handleBack = () => {
    navigate('/mise/companies');
  };

  // 企業詳細を取得
  const { data: company, isLoading: companyLoading, error: companyError } = useCompany(id || '');

  // アカウント一覧を取得
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError,
  } = useCompanyAccounts(id || '');

  // ユーザー一覧を取得
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useCompanyUsers(id || '');

  // タブ変更ハンドラー
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 削除ダイアログを開く
  const handleOpenDeleteDialog = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  // 削除ダイアログを閉じる
  const handleCloseDeleteDialog = () => {
    if (!forceDeleteCompany.isPending) {
      setDeleteDialogOpen(false);
      setDeleteError(null);
    }
  };

  // 強制削除を実行
  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleteError(null);

    try {
      await forceDeleteCompany.mutateAsync(id);
      setDeleteDialogOpen(false);
      navigate('/mise/companies');
    } catch (error) {
      console.error('企業の強制削除に失敗しました:', error);
      setDeleteError(error instanceof Error ? error.message : '企業の削除に失敗しました。');
    }
  };

  // 認証ローディング中
  if (authLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  // 未認証の場合
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  // 企業詳細のローディング中
  if (companyLoading) {
    return (
      <AppLayout showSearch={false}>
        <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
        </Box>
      </AppLayout>
    );
  }

  // 企業詳細のエラー
  if (companyError || !company) {
    return (
      <AppLayout showSearch={false}>
        <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
          <Alert severity="error">
            {companyError instanceof Error
              ? companyError.message
              : '企業データの読み込みに失敗しました。'}
          </Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout showSearch={false}>
      <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
        <Box
          sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Box>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              variant="outlined"
              sx={{ mb: 1 }}
            >
              企業一覧に戻る
            </Button>
            <Typography variant="h6" component="h1" color="text.secondary">
              企業詳細
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleOpenDeleteDialog}
          >
            企業を削除
          </Button>
        </Box>

        {/* 企業基本情報 */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="h6">基本情報</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Email />}
                onClick={() => setEmailTemplateDialogOpen(true)}
              >
                メールテンプレート参照
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  企業ID
                </Typography>
                <Typography variant="body1">{company.company_id}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* タブセクション */}
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="企業情報タブ"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab label={`アカウント一覧 (${accounts.length})`} {...a11yProps(0)} />
              <Tab label={`ユーザー一覧 (${users.length})`} {...a11yProps(1)} />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            <CompanyAccountList
              accounts={accounts}
              isLoading={accountsLoading}
              error={accountsError}
              companyId={id || ''}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <CompanyUserList
              users={users}
              isLoading={usersLoading}
              error={usersError}
              companyId={id || ''}
            />
          </TabPanel>
        </Box>

        {/* 企業削除確認ダイアログ */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <Warning color="error" />
            企業の削除確認
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              企業「<strong>{company.company_id}</strong>」を削除します。
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              この操作は元に戻せません。以下のデータがすべて削除されます。
            </Alert>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Person color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={`ユーザー（${company.user_count}件）`}
                  secondary="Cognitoアカウントも削除されます"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AccountTree color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={`アカウント紐づけ（${company.account_count}件）`}
                  secondary="アカウント自体は削除されません"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Settings color="action" />
                </ListItemIcon>
                <ListItemText primary="アカウント設定" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Cloud color="action" />
                </ListItemIcon>
                <ListItemText primary="Cognitoユーザープール" />
              </ListItem>
            </List>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={forceDeleteCompany.isPending}>
              キャンセル
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={forceDeleteCompany.isPending}
              startIcon={forceDeleteCompany.isPending ? <CircularProgress size={16} /> : <Delete />}
            >
              {forceDeleteCompany.isPending ? '削除中...' : '削除する'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* メールテンプレート参照ダイアログ */}
        <EmailTemplateViewDialog
          open={emailTemplateDialogOpen}
          onClose={() => setEmailTemplateDialogOpen(false)}
          companyId={id || ''}
        />
      </Box>
    </AppLayout>
  );
}
